import { z } from 'zod';

export const SITE_PALETTE_COLORS = ['background', 'surface', 'text', 'mutedText', 'primary', 'accent', 'success', 'danger'] as const;
export type SitePaletteColor = (typeof SITE_PALETTE_COLORS)[number];
export type SitePalette = Record<SitePaletteColor, string>;
export type SitePalettes = { light?: SitePalette | null; dark?: SitePalette | null };

export const DEFAULT_SITE_PALETTES: Record<'light' | 'dark', SitePalette> = {
  light: { background: '#edf1f9', surface: '#ffffff', text: '#16243d', mutedText: '#4f607c',
    primary: '#3556b8', accent: '#ffc23d', success: '#0b7a4f', danger: '#c8264a' },
  dark: { background: '#111a2e', surface: '#1a2740', text: '#f3f5fc', mutedText: '#b7c4da',
    primary: '#9aafff', accent: '#ffc23d', success: '#3ddc97', danger: '#ff6b86' },
};

function luminance(hex: string): number {
  const rgb = [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map((value) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  return rgb[0]! * 0.2126 + rgb[1]! * 0.7152 + rgb[2]! * 0.0722;
}

export function colorContrast(first: string, second: string): number {
  const a = luminance(first);
  const b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function paletteContrastValid(palette: SitePalette): boolean {
  return [palette.text, palette.mutedText].every((text) =>
    [palette.background, palette.surface].every((background) => colorContrast(text, background) >= 4.5));
}

const hexColor = z.string().length(7).regex(/^#[0-9a-fA-F]{6}$/, 'Choisis une couleur hexadécimale #RRGGBB.').transform((color) => color.toLowerCase());
export const sitePaletteSchema = z.strictObject({
  background: hexColor, surface: hexColor, text: hexColor, mutedText: hexColor,
  primary: hexColor, accent: hexColor, success: hexColor, danger: hexColor,
});

export const sitePalettesSchema = z.strictObject({
  light: sitePaletteSchema.nullable().optional(),
  dark: sitePaletteSchema.nullable().optional(),
});

export function parseSitePalettes(raw: string | null | undefined): SitePalettes | null {
  return raw == null ? null : sitePalettesSchema.parse(JSON.parse(raw));
}

function onColor(color: string): string {
  return colorContrast('#ffffff', color) >= colorContrast('#000000', color) ? '#ffffff' : '#000000';
}


export function sitePaletteTokens(palette: SitePalette): Record<string, string> {
  const ink = (color: string) => [palette.background, palette.surface].every((background) => colorContrast(color, background) >= 4.5)
    ? color : palette.text;
  return {
    '--bg': palette.background, '--surface': palette.surface, '--surface-2': palette.surface,
    '--sunken': palette.background, '--edge': palette.background,
    '--text': palette.text, '--text-soft': palette.mutedText, '--text-faint': palette.mutedText,
    '--line': palette.mutedText, '--panel-border': palette.mutedText,
    '--primary': palette.primary, '--primary-edge': palette.primary, '--on-primary': onColor(palette.primary),
    '--selected': palette.primary, '--selected-text': onColor(palette.primary), '--selected-ring': ink(palette.primary),
    '--link': ink(palette.primary), '--focus': ink(palette.primary),
    '--accent': palette.accent, '--xp': palette.accent, '--xp-ink': ink(palette.accent),
    '--reward': palette.accent, '--reward-edge': palette.accent, '--reward-ink': ink(palette.accent), '--on-reward': onColor(palette.accent),
    '--success': ink(palette.success), '--danger': ink(palette.danger),
  };
}
